import { useState, useEffect } from 'react';
import { Building2, Users, Trash2, Edit2, Plus, Check, X, CreditCard, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Workspace, WorkspaceMember } from '../types';
import { getWorkspaceMembers } from '../services/firestore';
import { SEO } from '../components/shared/SEO';

export function WorkspacesPage() {
  const { workspaces, currentWorkspace, createWorkspace, switchWorkspace, updateWorkspaceSettings } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<string | null>(null);
  const [workspaceMembers, setWorkspaceMembers] = useState<{ [key: string]: WorkspaceMember[] }>({});
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'free' as const,
    settings: {
      currency: 'USD',
      taxRate: 0,
      invoicePrefix: 'INV',
      paymentMethods: [],
      branding: {
        primaryColor: '#3B82F6',
        companyName: '',
        address: '',
        email: '',
        phone: '',
        mobile: '',
        website: '',
        taxId: '',
        licenseNumber: '',
        city: '',
        postalCode: ''
      }
    }
  });

  useEffect(() => {
    const loadMembers = async () => {
      const membersMap: { [key: string]: WorkspaceMember[] } = {};
      for (const workspace of workspaces) {
        const members = await getWorkspaceMembers(workspace.id);
        membersMap[workspace.id] = members;
      }
      setWorkspaceMembers(membersMap);
    };
    loadMembers();
  }, [workspaces]);

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createWorkspace({
        name: formData.name,
        slug: formData.slug,
        settings: formData.settings
      });
      setFormData({
        name: '',
        slug: '',
        plan: 'free',
        settings: {
          currency: 'USD',
          taxRate: 0,
          invoicePrefix: 'INV',
          paymentMethods: [],
          branding: {
            primaryColor: '#3B82F6',
            companyName: '',
            address: '',
            email: '',
            phone: '',
            mobile: '',
            website: '',
            taxId: '',
            licenseNumber: '',
            city: '',
            postalCode: ''
          }
        }
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating workspace:', error);
      alert('Failed to create workspace. Please try again.');
    }
  };

  const handleNameChange = (name: string) => {
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setFormData(prev => ({
      ...prev,
      name,
      slug,
      settings: {
        ...prev.settings,
        branding: {
          ...prev.settings.branding,
          companyName: name
        }
      }
    }));
  };

  return (
    <>
      <SEO
        title="Workspaces"
        description="Manage your workspaces, team members, and business settings in FreelancePay. Organize your freelance business efficiently."
        keywords={["workspaces", "team management", "freelance business", "settings", "FreelancePay"]}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Manage Workspaces</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Workspace
          </button>
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Create New Workspace</h2>
              <form onSubmit={handleCreateWorkspace} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    placeholder="My Workspace"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workspace URL
                  </label>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      freelancepay.app/
                    </span>
                    <input
                      type="text"
                      placeholder="my-workspace"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Create Workspace
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {workspaces.map((workspace) => (
              <li key={workspace.id}>
                <div className="px-6 py-5 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {workspace.logo ? (
                      <img
                        src={workspace.logo}
                        alt={workspace.name}
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {workspace.name}
                        {workspace.id === currentWorkspace?.id && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        )}
                      </h3>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          {workspaceMembers[workspace.id]?.length || 0} members
                        </div>
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-1" />
                          freelancepay.app/{workspace.slug}
                        </div>
                        <div className="flex items-center">
                          <CreditCard className="h-4 w-4 mr-1" />
                          {workspace.plan || 'Free'} Plan
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {workspace.id !== currentWorkspace?.id && (
                      <button
                        onClick={() => switchWorkspace(workspace.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Switch to
                      </button>
                    )}
                    <button
                      onClick={() => setEditingWorkspace(workspace.id)}
                      className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-50"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
} 